import {ApolloError} from 'apollo-client';
import {useMemo} from 'react';

import {envIsLocal} from '../../config';
import {useOrganizationSubscriptionsBasicInfoQuery} from '../../generated/graphql';
import {useSelectedAccountEntityOrg} from '../../graphql/entityOrg';
import {useViewer} from '../../state/viewer/hooks';
import {useAccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {useIsWorkspaceTemplatePrivilegeEnabled} from './useIsWorkspaceTemplateEnabled';
import {
  useWorkspaceTemplatesQueryResult,
  WorkspaceTemplate,
} from './workspaceTemplatesQueries';

/**
 * This hook is used to fetch the workspace templates the user created in the selected account.
 */
export const useViewerWorkspaceTemplates = (
  isWorkspaceTemplateDisplayed: boolean
): {
  entityOrgId: string | undefined;
  workspaceTemplates: WorkspaceTemplate[];
  isLoadingWorkspaceTemplates: boolean;
  workspaceTemplatesError: ApolloError | undefined;
  refetchWorkspaceTemplates: () => void;
} => {
  const viewer = useViewer();

  // Grab the the organization's entity id to check if the entity is on the enterprise plan
  // This is the corresponding entity row where entityType = 'organization' and
  // organization_id  = selectedAccount?.id
  const isWorkspaceTemplateAccountPrivilegeEnabled =
    useIsWorkspaceTemplatePrivilegeEnabled();
  const {entityOrgId, isLoadingEntityOrg} = useEntityOrgForWorkspaceTemplates(
    isWorkspaceTemplateAccountPrivilegeEnabled && isWorkspaceTemplateDisplayed
  );

  const {
    workspaceTemplates,
    isLoadingWorkspaceTemplates,
    workspaceTemplatesError,
    refetchWorkspaceTemplates,
  } = useWorkspaceTemplatesQueryResult({
    entityId: entityOrgId,
    userId: viewer?.id,
    skip:
      !isWorkspaceTemplateAccountPrivilegeEnabled ||
      !isWorkspaceTemplateDisplayed,
  });

  const isLoading = isLoadingEntityOrg || isLoadingWorkspaceTemplates;

  return useMemo(() => {
    if (isLoading) {
      return {
        entityOrgId,
        isLoadingWorkspaceTemplates,
        workspaceTemplates: [],
        workspaceTemplatesError: undefined,
        refetchWorkspaceTemplates: () => {},
      };
    }

    if (workspaceTemplatesError != null) {
      return {
        entityOrgId,
        isLoadingWorkspaceTemplates,
        workspaceTemplates: [],
        workspaceTemplatesError,
        refetchWorkspaceTemplates: () => {},
      };
    }

    return {
      entityOrgId,
      isLoadingWorkspaceTemplates,
      workspaceTemplates,
      workspaceTemplatesError,
      refetchWorkspaceTemplates,
    };
  }, [
    entityOrgId,
    isLoading,
    isLoadingWorkspaceTemplates,
    refetchWorkspaceTemplates,
    workspaceTemplates,
    workspaceTemplatesError,
  ]);
};

export const useEntityOrgForWorkspaceTemplates = (
  isWorkspaceTemplatesEnabled: boolean
): {
  isLoadingEntityOrg: boolean;
  entityOrgId: string | undefined;
  orgName: string | undefined;
} => {
  // accounts are only a concept in saas right now
  // for now, we'll directly query the organization subscription query and manually grab the org account for server

  // On server, there are 2 possible accounts. One personal and one for the hardcoded "server" org.
  // It is safe to check against "server" org name because this is hardcoded in the env.
  // To handle the personal account case on server, we'll just check against the entity name since
  // the account selector component doesn't exist on server.
  const {data: viewerOrgsResult, loading: isOrgSubscriptionLoading} =
    useOrganizationSubscriptionsBasicInfoQuery({
      skip: !envIsLocal || !isWorkspaceTemplatesEnabled,
    });
  const serverOrg = useMemo(
    () =>
      viewerOrgsResult?.viewer?.organizations.find(
        org => org.name === 'server'
      ),
    [viewerOrgsResult]
  );

  const {selectedAccount} = useAccountSelectorContext();
  const {entityOrg, isLoadingEntityOrg} = useSelectedAccountEntityOrg({
    orgId: envIsLocal ? serverOrg?.id : selectedAccount?.id,
    skip:
      !isWorkspaceTemplatesEnabled || (envIsLocal && isOrgSubscriptionLoading),
  });

  if (isLoadingEntityOrg || isOrgSubscriptionLoading) {
    return {
      isLoadingEntityOrg: true,
      entityOrgId: undefined,
      orgName: undefined,
    };
  }

  return {
    isLoadingEntityOrg: false,
    entityOrgId: entityOrg?.id,
    orgName: entityOrg?.name,
  };
};
